//node_modules
import React, { FunctionComponent, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { cx, css } from "@adjust/libs/emotion";
import { formatDistanceToNowStrict } from "date-fns";
import Link from "next/link";

//components
import MentionImageComponent from "./MentionImageComponent";

//types
import type { MentionDocument } from "@adjust/libs/database/mentions";

type CardProps = {
  mention: MentionDocument;
};

const cardLinkCSS = css({
  display: "block",
  width: "100%",
  height: "100%",
  borderRadius: 20,
  overflow: "hidden",
  textDecoration: "none",
  color: "#111827",
  background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
  border: "1px solid rgba(17, 24, 39, 0.06)",
  boxShadow: "0 8px 24px rgba(17, 24, 39, 0.06), 0 2px 8px rgba(17, 24, 39, 0.04)",
  transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, background 220ms ease",
  position: "relative",

  ":hover": {
    color: "#111827",
    textDecoration: "none",
    transform: "translateY(-4px)",
    borderColor: "rgba(59, 130, 246, 0.16)",
    boxShadow: "0 18px 40px rgba(17, 24, 39, 0.10), 0 6px 18px rgba(17, 24, 39, 0.06)",
    background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
  },

  ":focus-visible": {
    outline: "3px solid rgba(59, 130, 246, 0.35)",
    outlineOffset: 2,
  },

  ":hover .mention-image-inner": {
    transform: "scale(1.04)",
  },

  ":hover .mention-title": {
    color: "#0f172a",
  },
});

const cardBodyCSS = css({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const imageWrapCSS = css({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 9",
  overflow: "hidden",
  background: "linear-gradient(135deg, rgba(241,245,249,1) 0%, rgba(226,232,240,1) 100%)",
  borderBottom: "1px solid rgba(17, 24, 39, 0.06)",
});

const imageInnerCSS = css({
  width: "100%",
  height: "100%",
  transition: "transform 300ms ease",
});

const contentCSS = css({
  padding: "1rem 1rem 1.1rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  flexGrow: 1,
});

const metaRowCSS = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  minHeight: 22,
});

const domainPillCSS = css({
  display: "inline-flex",
  alignItems: "center",
  maxWidth: "100%",
  borderRadius: 999,
  padding: "0.22rem 0.6rem",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  background: "rgba(15, 23, 42, 0.05)",
  color: "#334155",
  border: "1px solid rgba(15, 23, 42, 0.06)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontFamily: '"DM Sans", sans-serif',
});

const dateCSS = css({
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "0.78rem",
  fontWeight: 500,
  color: "#6b7280",
  whiteSpace: "nowrap",
});

const titleCSS = css({
  margin: 0,
  fontFamily: '"DM Sans", sans-serif',
  fontWeight: 700,
  fontSize: "1rem",
  lineHeight: 1.35,
  color: "#111827",
  letterSpacing: "-0.01em",
  transition: "color 180ms ease",

  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  minHeight: "4.05em", // ~3 lines for better grid consistency
});

const footerHintCSS = css({
  marginTop: "auto",
  paddingTop: "0.35rem",
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "#374151",
  opacity: 0.9,
});

const arrowCSS = css({
  transition: "transform 180ms ease",
  selectors: {
    [`a:hover &`]: {
      transform: "translateX(2px)",
    },
  },
});

const colCSS = css({
  display: "flex",
});

const MentionTitleComponent: FunctionComponent<CardProps> = ({ mention }) => {
  return (
    <Card.Title as="h4" className={cx("mention-title", titleCSS)}>
      {mention.title}
    </Card.Title>
  );
};

const MentionComponent: FunctionComponent<CardProps> = ({ mention }) => {
  const hasPublishedTime = Boolean(mention.publishedTime);
  const domainResult = useMemo(() => new URL(mention.url).hostname, [mention.url]);

  return (
    <Col md={6} lg={4} xl={3} className={cx("p-3", colCSS)}>
      <Card as={Link} href={mention.url} target="_blank" rel="noopener noreferrer" className={cardLinkCSS}>
        <Card.Body className={cardBodyCSS}>
          <div className={imageWrapCSS}>
            <div className={cx("mention-image-inner", imageInnerCSS)}>
              <MentionImageComponent src={mention.imgSrc} />
            </div>
          </div>

          <div className={contentCSS}>
            <div className={metaRowCSS}>
              {domainResult ? <span className={domainPillCSS}>{domainResult}</span> : <span />}

              {hasPublishedTime ? (
                <span className={dateCSS}>
                  {formatDistanceToNowStrict(new Date(mention.publishedTime), {
                    addSuffix: true,
                  })}
                </span>
              ) : null}
            </div>

            <MentionTitleComponent mention={mention} />

            <div className={footerHintCSS}>
              <span>Read article</span>
              <span className={arrowCSS} aria-hidden="true">
                →
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default React.memo(MentionComponent);
